import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { errorMessage, formatMoney, removeTrailingZeros } from "helpers/common";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { fetchFields, getBalancedScorecardFields, getCalculationRelatedFields, getFixedFields, getFreeFields } from "store/fieldsSlice";
import { api } from "helpers/axios";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import * as Sentry from "@sentry/react";
import { fetchSavedFilters, getSavedFilters } from "store/savedFiltersSlice";
import { toast } from "helpers/toast";
import sanitize from "helpers/sanitize";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import EmployeeTableColumn from "components/EmployeeManagement/EmployeeTableColumn";
import { tableResize } from "helpers/tableResize";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import { useOutletContext, useSearchParams } from "react-router-dom";
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, DateInput, Field, Input, Modal, Select } from "components/ui";
import { helpTextIcon } from "helpers/helpText";
import { useTranslation } from "translation/useTranslation";
import { periodActivityTranslationFallback } from "helpers/periodActivityTranslations";
import { getLang } from "store/userSlice";
import ListPagination from "components/ui/ListPagination";

export default function Filters() {
  const dispatch = useDispatch();

  const lang = useSelector(getLang);

  const { helps } = useOutletContext();

  const { translate, editableTranslate } = useTranslation();

  const savedFilters = useSelector(getSavedFilters);
  const fixedFields = useSelector(getFixedFields);
  const freeFields = useSelector(getFreeFields);
  const bsFields = useSelector(getBalancedScorecardFields);
  const calculationRelatedFields = useSelector(getCalculationRelatedFields);

  const [searchParams, setSearchParams] = useSearchParams();

  const [error, setError] = useState(null);

  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingApplyFilters, setLoadingApplyFilters] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [filterFields, setFilterFields] = useState([]); // store available fields to filter
  const [filterOperators, setFilterOperators] = useState([]); // store available operators

  const [fieldOptions, setFieldOptions] = useState([]); // react-select options

  const [allTableFields, setAllTableFields] = useState([]);
  const [tableFields, setTableFields] = useState([]); // table columns

  const [selectedSavedFilter, setSelectedSavedFilter] = useState(null);

  const [appliedFilters, setAppliedFilters] = useState(null);

  const [showFieldsDropdown, setShowFieldsDropdown] = useState(false);

  const [employees, setEmployees] = useState(null);

  const [sortCols, setSortCols] = useState([['last_name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    perPage: 20
  });

  const tableRef = useRef(null);
  const theadRef = useRef(null);

  const didPreselect = useRef(false);

  const savedFilterOptions = useMemo(() => {
    const savedArr = [];
    const systemArr = [];

    if (savedFilters) {
      savedFilters.forEach(s => {
        if (s?.type === 'system') {
          systemArr.push({
            label: s?.translation_key ? translate(s.translation_key, periodActivityTranslationFallback(s.translation_key, s.name)) : s.name,
            value: s.hash_id,
            is_system: true
          });
        } else {
          savedArr.push({
            label: s?.translation_key ? translate(s.translation_key, periodActivityTranslationFallback(s.translation_key, s.name)) : s.name,
            value: s.hash_id,
            is_system: false
          });
        }
      });
    }

    return [
      {
        label: translate('filters.saved_filters'),
        options: savedArr
      },
      {
        label: translate('filters.system_filters'),
        options: systemArr
      }
    ];
  }, [savedFilters]);

  const flatSavedFilterOptions = useMemo(() => {
    return savedFilterOptions.flatMap((g) => g?.options || []);
  }, [savedFilterOptions]);

  const emptyFilterForm = () => ({ filter_name: null, filters: [{ key: null, operator: null, value: null }] });

  const handleSelectSaved = (val) => {
    setSelectedSavedFilter(val);

    const selected = savedFilters.filter(s => s.hash_id === val.value)[0];
    const filters = selected.filters.map(filter => {
      const opt = filterOperators.filter(o => o.key === filter.operator)[0];

      return {
        key: fieldOptions.filter(o => o.value === filter.key)[0] || null,
        operator: opt ? { label: editableTranslate(opt.translation_key, opt.label), value: opt.key } : null,
        value: filter.value
      };
    });
    reset({ filter_name: selected.name, filters: filters });

    setAppliedFilters(null);
    setEmployees(null);
  };

  const handleAddNew = () => {
    setSelectedSavedFilter(null);
    reset(emptyFilterForm());
    setAppliedFilters(null);
    setEmployees(null);

    if (searchParams.get('hash_id')) setSearchParams({}, { replace: true });
  };

  const fetchAvailableFilters = async () => {
    try {
      const { data, status } = await api.get('/employees/available-filters');
      if (status === 200) {
        setFilterFields(data.data);

        const operators = [];

        const fOptions = data.data.map(f => {
          f.operators.forEach(opt => {
            let found = false;
            operators.forEach(o => {
              if (o.key === opt.key) found = true;
            });
            if (!found) operators.push(opt);
          });

          return {
            label: f?.custom_name || editableTranslate(f.translation_key, periodActivityTranslationFallback(f.translation_key, f.label)),
            value: f.key
          };
        });

        setFieldOptions(fOptions);

        setFilterOperators(operators);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch available filters'));
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    if (!savedFilters) dispatch(fetchSavedFilters());

    if (!fixedFields || !freeFields || !bsFields || !calculationRelatedFields) dispatch(fetchFields());

    fetchAvailableFilters();
  }, []);

  useEffect(() => {
    const aFields = [];
    const tFields = [];

    if (fixedFields) {
      fixedFields.filter(f => f.is_active).forEach(f => {
        const key = f.translation_key.replace('field.', '');
        const obj = { ...f, key };

        aFields.push(obj);

        if (f.display_in.includes('employee_management')) tFields.push(obj);
      });
    }

    if (calculationRelatedFields) {
      calculationRelatedFields.filter(f => f.is_active).forEach(f => {
        const key = f.translation_key.replace('field.', '');
        const obj = { ...f, key };

        aFields.push(obj);

        if (f.display_in.includes('employee_management')) tFields.push(obj);
      });
    }

    if (bsFields) {
      bsFields.filter(f => f.is_active).forEach(f => {
        const key = f.translation_key.replace('field.', '');
        const obj = { ...f, key };

        aFields.push(obj);

        if (f.display_in.includes('employee_management')) tFields.push(obj);
      });
    }

    if (freeFields) {
      freeFields.filter(f => f.is_active).forEach(f => {
        const key = f.translation_key.replace('field.', '');
        const obj = { ...f, key };

        aFields.push(obj);

        if (f.display_in.includes('employee_management')) tFields.push(obj);
      });
    }

    setAllTableFields(aFields);
    setTableFields(tFields);
  }, [fixedFields, freeFields, bsFields, calculationRelatedFields]);

  // Preselect the deep-linked filter once. Without the ref this re-fires after the header
  // add button clears the selection and puts the saved filter straight back.
  useEffect(() => {
    if (didPreselect.current) return;

    const hashId = searchParams.get('hash_id');
    if (!hashId) return;

    if (!selectedSavedFilter && flatSavedFilterOptions.length > 0 && filterOperators.length > 0) {
      const opts = flatSavedFilterOptions.filter(opt => opt.value === hashId);
      if (opts.length > 0) {
        didPreselect.current = true;
        handleSelectSaved(opts[0]);
      }
    }
  }, [searchParams, flatSavedFilterOptions, filterOperators, selectedSavedFilter]);

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 250,
    email: 300,
    individual_pay_factor: 250,
    individual_average_rating: 300,
    exclude_from_payout: 300,
    balanced_scorecard_template: 400,
    lk: 100
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'filters', false, {}, maxColWidth, setResizedCount);
  }, [employees, triggerColWidth, tableFields]);

  const schema = yup.object({
    filter_name: yup.string().required().label(translate('filters.template_name')),
    filters: yup.array().of(yup.object().shape({
      key: yup.object().required().label(translate('batch_update.field')),
      operator: yup.object().required().label(translate('batch_update.operator')),
      value: yup.string().notRequired().label(translate('batch_update.value'))
    })).min(1).required()
  });

  const { control, register, reset, getValues, watch, trigger, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      filters: [{ value: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters"
  });

  const watchFilters = useWatch({
    control,
    name: 'filters'
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    setResizedCount(c => c + 1);

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const handleScrollTable = () => {
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const theadParentCoordinates = theadEl.parentElement.getBoundingClientRect();
      if (theadParentCoordinates.y < navHeight) {
        theadEl.style.transform = `translate3d(0, ${-theadParentCoordinates.y + navHeight}px, 0)`;
      } else {
        theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [employees, tableFields]);

  const getFilterField = (index) => {
    const key = watchFilters[index]?.key?.value;
    if (!key) return null;
    return filterFields.find(f => f.key === key) || null;
  };

  const getOperatorOptions = (index) => {
    if (watchFilters[index]?.key) {
      const field = filterFields.filter(f => f.key === watchFilters[index].key.value)[0];
      return field.operators.map(o => ({
        label: editableTranslate(o.translation_key, o.label),
        value: o.key
      }));
    }
    return [];
  };

  const getOperatorValue = (index, value) => {
    if (watchFilters[index]?.key && value) {
      const filtered = getOperatorOptions(index).filter(o => o.value === value.value);
      if (filtered.length > 0) return filtered[0];
    }
    return null;
  };

  const handleRemoveRow = (index) => {
    if (fields.length > 1) remove(index);
  };

  const handleSaveFilters = async (e, asNew = false) => {
    e?.preventDefault();
    e?.currentTarget?.blur();

    setError(null);

    if (await trigger()) {
      setLoadingSave(true);

      const filters = getValues('filters').map(f => ({
        key: f.key.value,
        operator: f.operator.value,
        value: f.value
      }));

      try {
        const formData = {
          name: getValues('filter_name'),
          filters,
          model: 'employee'
        };

        if (selectedSavedFilter && !asNew) formData.hash_id = selectedSavedFilter.value; // Update

        const { status, data } = await api.post(`/saved-filters`, formData);
        if (status === 200) {
          toast.success(translate('form.success.saved'));

          setSelectedSavedFilter({
            label: data.name,
            value: data.hash_id,
            is_system: false
          });
          dispatch(fetchSavedFilters());
        }

        setLoadingSave(false);
      } catch (err) {
        setLoadingSave(false);

        setError(errorMessage(err, 'save filters'));
        Sentry.captureException(err);
      }
    }
  };

  const handleApplyFilters = async (reset = false) => {
    if (await trigger('filters')) {
      setEmployees(null);

      const filters = getValues('filters');
      setAppliedFilters(filters);
      setLoadingApplyFilters(true);
      fetchEmployeesWithFilter(filters, reset);
    }
  };

  const fetchEmployeesWithFilter = async (rawFilters, reset = false) => {
    const filters = rawFilters.map(f => {
      return {
        key: f.key.value,
        operator: f.operator.value,
        value: f.value
      };
    });

    let page = 1;
    if (!reset) page = pagination.page;

    try {
      let url = `/employees?limit=${pagination.perPage}&page=${page}`;
      if (sortCols && sortCols.length > 0) {
        url += `&sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }
      const { data, status } = await api.post(url, { filters });
      if (status === 200) {
        const newData = data.data.map((item) => {
          const information = {};

          item.employeeInformation.data.forEach(info => {
            information[info.key] = info.value;
          });

          return { ...item, information };
        });

        setEmployees(newData);

        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: +temp.total_pages, perPage: +temp.per_page });
      }
      setLoadingApplyFilters(false);
    } catch (err) {
      setLoadingApplyFilters(false);
      setError(errorMessage(err, 'fetch employees with filters'));
      Sentry.captureException(err);
    }
  };

  const handleColumnCheck = (hashId, e) => {
    const curTFields = [...tableFields];

    if (e.target.checked) {
      const field = allTableFields.filter(f => f.hash_id === hashId)[0];
      curTFields.push(field);
      setTableFields(curTFields);
    } else if (tableFields.length > 1) {
      setTableFields(curTFields.filter(f => f.hash_id !== hashId));
    }
  };

  const handleDelete = async () => {
    setLoadingDelete(true);
    try {
      const { status } = await api.delete(`/saved-filters/${selectedSavedFilter.value}`);
      if (status === 200) {
        setSelectedSavedFilter(null);
        reset(emptyFilterForm());
        setAppliedFilters(null);
        setEmployees(null);
        dispatch(fetchSavedFilters());
      }
      setLoadingDelete(false);
      setShowDeleteConfirm(false);
    } catch (err) {
      setLoadingDelete(false);
      setShowDeleteConfirm(false);
      setError(errorMessage(err, 'delete saved filter'));
      Sentry.captureException(err);
    }
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field.slug) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field.slug, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const getSortCol = (field) => {
    for (const sc of sortCols) {
      if (sc[0] === field.slug) return sc;
    }
    return null;
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field.slug) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field.slug);
      setSortCols(sortCols.filter(sc => sc[0] === field.slug));
    }
  };

  useEffect(() => {
    if (employees) handleApplyFilters(true);

    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  useEffect(() => {
    if (employees) handleApplyFilters();
  }, [pagination.page, pagination.perPage]);

  const getLabel = (key, translation_key, fallback) => {
    const sKey = key.replaceAll('.', '-');
    const help = helps && helps.filter(h => h.key === sKey)[0] || null;

    return <>
      {editableTranslate(translation_key, fallback)}
      {help && helpTextIcon(sKey, help.short_text, 'text-normal')}
    </>
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div className="d-flex justify-content-between">
            <div>
              <h4>{editableTranslate('global.menu.settings.filters')}</h4>
              <p className="section-description">{editableTranslate('filters.description')}</p>
            </div>
            <div>
              <Button onClick={handleAddNew} variant="secondary" className="text-nowrap" data-testid="add-button">
                <i className="bi bi-plus"></i> {editableTranslate('form.add_new')}
              </Button>
            </div>
          </div>

          <div className="settings-batch-update mt-4">
            {
              error &&
              <div className="mt-3">
                <ErrorAlert error={error} onClose={() => setError(null)} />
              </div>
            }

            <div className="gray-box filters-box">
              <label className="question-label font-weight-bold d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  {getLabel('filters-overview', 'form.filter', 'Filters')}
                </div>
                <div className="d-flex align-items-center">
                  {
                    flatSavedFilterOptions.length > 0 && <>
                      <span className="mr-2">{editableTranslate('filters.templates')}:</span>
                      <div style={{ display: 'contents' }} data-testid="saved-filter-select">
                        <Select className="min-w-300 peb-rs--grouped" placeholder={translate('form.select_placeholder')} options={savedFilterOptions} value={selectedSavedFilter} onChange={handleSelectSaved} />
                      </div>
                    </>
                  }
                </div>
              </label>
              <div className="px-3 py-2 pb-3">
                <form>
                  <div className="form-row filters-table-row">
                    <div className="col"><label className="peb-field__label">{editableTranslate('batch_update.field')}</label></div>
                    <div className="col"><label className="peb-field__label">{editableTranslate('batch_update.operator')}</label></div>
                    <div className="col"><label className="peb-field__label">{editableTranslate('batch_update.value')}</label></div>
                  </div>
                  {
                    fields.map((f, index) => {
                      return <div key={f.id} className="form-row">
                        <div className="form-group col">
                          <Controller
                            name={`filters.${index}.key`}
                            control={control}
                            render={({ field: { onChange, onBlur, value, ref } }) => <div style={{ display: 'contents' }} data-testid="filter-field-select">
                              <Select error={!!errors?.filters?.[index]?.key} placeholder={translate('form.select_placeholder')} options={fieldOptions} isClearable={true} onChange={onChange} onBlur={onBlur} ref={ref} value={value ? fieldOptions.filter(o => o.value === value.value)[0] : null} />
                            </div>}
                          />
                          {errors?.filters?.[index]?.key && <div className="peb-field__error">{errors.filters[index].key.message}</div>}
                        </div>
                        <div className="form-group col">
                          <Controller
                            name={`filters.${index}.operator`}
                            control={control}
                            render={({ field: { onChange, onBlur, value, ref } }) => <div style={{ display: 'contents' }} data-testid="filter-operator-select">
                              <Select error={!!errors?.filters?.[index]?.operator} placeholder={translate('form.select_placeholder')} options={getOperatorOptions(index)} isClearable={true} onChange={onChange} onBlur={onBlur} ref={ref} value={getOperatorValue(index, value)} />
                            </div>}
                          />
                          {errors?.filters?.[index]?.operator && <div className="peb-field__error">{errors.filters[index].operator.message}</div>}
                        </div>
                        <div className="form-group col">
                          <div className="d-flex">
                            <div className="w-100">
                              {
                                (() => {
                                  const field = getFilterField(index);
                                  if (field?.available_values) {
                                    const options = Object.entries(field.available_values).map(([value, translationKey]) => ({
                                      value,
                                      label: editableTranslate(translationKey, periodActivityTranslationFallback(translationKey))
                                    }));

                                    return (
                                      <Controller
                                        name={`filters.${index}.value`}
                                        control={control}
                                        render={({ field: { onChange, onBlur, value, ref } }) => (
                                          <div style={{ display: 'contents' }} data-testid="filter-value-select">
                                            <Select
                                              error={!!errors?.filters?.[index]?.value}
                                              options={options}
                                              isClearable={true}
                                              onChange={(opt) => onChange(opt ? opt.value : '')}
                                              onBlur={onBlur}
                                              ref={ref}
                                              value={options.find(o => o.value === value) || null}
                                              placeholder={translate('form.select_placeholder')}
                                            />
                                          </div>
                                        )}
                                      />
                                    );
                                  }

                                  if (field?.type === 'date') {
                                    return <DateInput className="col" error={!!errors?.filters?.[index]?.value} {...register(`filters.${index}.value`)} maxLength={255} />;
                                  }

                                  return <Field error={errors?.filters?.[index]?.value?.message}><Input type="text" className="col" error={!!errors?.filters?.[index]?.value} {...register(`filters.${index}.value`)} maxLength={255} /></Field>;
                                })()
                              }
                            </div>
                            <div className="col-auto d-flex action-btn-div pl-2 pr-0">
                              <Button onClick={() => handleRemoveRow(index)} variant="ghost" size="inline" icon="bi-x-square-fill" className="btn-remove-filter" data-testid="cancel-button" />
                            </div>
                          </div>
                        </div>
                      </div>
                    })
                  }
                  <div className="text-center">
                    <Button onClick={() => append({ value: '' })} variant="ghost" size="inline" icon="bi-plus" className="font-weight-bold red-dark" data-testid="add-condition-button">{editableTranslate('filters.add_condition')}</Button>
                  </div>
                </form>
              </div>
              <div className={`d-flex py-3 px-3 mt-3 filters-footer ${(selectedSavedFilter && !selectedSavedFilter?.is_system) ? 'justify-content-between' : 'justify-content-end'}`}>
                {
                  (selectedSavedFilter && !selectedSavedFilter?.is_system) && <Button variant="ghost" size="inline" tone="danger" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-button">{editableTranslate('global.delete')}</Button>
                }
                <div className="d-flex align-items-center">
                  <div className="d-flex align-items-center mr-4">
                    <div>
                      <Field error={errors?.filter_name?.message}>
                        <Input type="text" error={!!errors?.filter_name} placeholder={translate('filters.template_name')} {...register('filter_name')} data-testid="filter-name-input" />
                      </Field>
                    </div>
                  </div>
                  {
                    (!selectedSavedFilter || !selectedSavedFilter?.is_system) && <Button variant="primary" onClick={handleSaveFilters} data-testid="save-button" className="mr-2">{editableTranslate('button.save')}</Button>
                  }
                  {
                    selectedSavedFilter && <Button data-testid="save-as-new-filter-button" variant="secondary" onClick={e => handleSaveFilters(e, true)} className="mr-2">{editableTranslate('button.save_as_new_filter')}</Button>
                  }
                  <Button onClick={() => handleApplyFilters(true)} variant="secondary" className="text-nowrap" loading={loadingApplyFilters} data-testid="apply-filters-button">
                    {editableTranslate('button.apply_filters')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="mt-4">
              {
                appliedFilters && <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0"><span>{pagination?.totalRows || 0}</span> {editableTranslate('batch_update.found_count')}</h5>
                    <div className="btn-group">
                      <Button variant="secondary" className="ml-3" icon="bi-layout-three-columns" onClick={() => setShowFieldsDropdown(!showFieldsDropdown)} data-testid="columns-button">{editableTranslate('batch_update.columns')} <i className="bi bi-chevron-down"></i></Button>
                      <div className={`dropdown-menu dropdown-menu-right fields-dropdown p-3 ${showFieldsDropdown ? 'show' : ''}`}>
                        <div className="fields-dropdown-div">
                          {
                            allTableFields.map((field, i) => {
                              return <div key={i} className="form-group form-check">
                                <input onChange={(e) => handleColumnCheck(field.hash_id, e)} type="checkbox" className="form-check-input" id={'check_' + field.hash_id} checked={tableFields.filter(f => f.hash_id === field.hash_id).length > 0 ? true : false} data-testid={`column-check-${field.hash_id}`} />
                                <label className="ml-1" htmlFor={'check_' + field.hash_id}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</label>
                              </div>
                            })
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive has-custom-scrollbar">
                    <table ref={tableRef} className="table employees-table" data-testid="employees-table">
                      <thead ref={theadRef} className="sticky-top">
                        <tr>
                          {
                            tableFields.map((field, i) => {
                              const sc = getSortCol(field);

                              return <th key={i} scope="col" className={`cursor-pointer ${field.alignment ? 'text-' + field.alignment : ''}`}>
                                <span className="float-right floating-icons">
                                  <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${sc && sc[0] === field.slug ? (sc[1] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field)} data-testid={`sort-${field.slug}`} />
                                  {
                                    (sc && sc[0] === field.slug) && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field)} data-testid={`sort-pin-${field.slug}`} />
                                  }
                                </span>
                                <span data-testid={`sort-${field.slug}-label`} className="th-span" onClick={() => handleChangeSortCol(field)}>
                                  {field?.custom_name || editableTranslate(field.translation_key, field.name)}
                                  <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                  <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                                </span>
                                <div className="th-resize-div" data-field={field.slug}></div>
                              </th>
                            })
                          }
                        </tr>
                      </thead>
                      <tbody>
                        {
                          Array.isArray(employees) && employees.map((employee, index) => {
                            return <tr key={index} className="table-row no-select">
                              {
                                tableFields.map((field, i) => {
                                  return <EmployeeTableColumn key={i} field={field} employee={employee} displayLkTag={true} resizedCount={resizedCount} />
                                })
                              }
                            </tr>
                          })
                        }
                        {
                          Array.isArray(employees) && employees.length === 0 && <tr>
                            <td colSpan={tableFields.length} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                          </tr>
                        }
                        {
                          !employees && <tr>
                            <td colSpan={tableFields.length}>
                              <div className="d-flex justify-content-center" data-testid="loading">
                                <div className="spinner-border" role="status">
                                  <span className="sr-only">Wird geladen...</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                  <TableScrollbar tableRef={tableRef} data={employees} trigger={resizedCount} />

                  <ListPagination
                    page={pagination.page}
                    perPage={pagination.perPage}
                    totalPages={pagination.totalPages}
                    totalRows={pagination.totalRows}
                    setPage={(page) => setPagination({ ...pagination, page })}
                    setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
                  />
                </>
              }
            </div>

            <div className="py-5"></div>

            <Modal
              open={showDeleteConfirm}
              danger
              loading={loadingDelete}
              title={editableTranslate('batch_update.delete_confirmation_title', 'Ansichtsfilter löschen?')}
              cancelLabel={editableTranslate('form.no')}
              okLabel={editableTranslate('form.delete.yes')}
              onClose={() => setShowDeleteConfirm(false)}
              onConfirm={handleDelete}
              testIdPrefix="filters-delete-"
            >
              {editableTranslate('batch_update.delete_confirmation')}
            </Modal>
          </div>
        </div>
      </div>
    </>
  );
}
